import { useEffect, useRef } from 'react';

/**
 * OilSwab — CS2-style inky oil slick.
 *
 * GPU Navier-Stokes fluid simulation (ping-pong FBOs, classic GPU Gems pipeline)
 * rendered through an iridescent thin-film palette on a black base.
 *
 * Pipeline per frame:
 *   1. Optional mouse splat — injects velocity + dye where the cursor moved.
 *   2. Curl + vorticity confinement — adds swirling detail.
 *   3. Divergence of velocity.
 *   4. Jacobi pressure solve (PRESSURE_ITER iterations).
 *   5. Subtract pressure gradient → divergence-free velocity.
 *   6. Advect velocity (semi-Lagrangian, with dissipation).
 *   7. Advect dye (semi-Lagrangian, with dissipation).
 *   8. Display — render dye through oil palette.
 *
 * Velocity, dye, and pressure all live in half-float ping-pong framebuffers.
 */

const BASE_VERT = `
attribute vec2 position;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
uniform vec2 u_texelSize;
void main() {
  vUv = position * 0.5 + 0.5;
  vL = vUv - vec2(u_texelSize.x, 0.0);
  vR = vUv + vec2(u_texelSize.x, 0.0);
  vT = vUv + vec2(0.0, u_texelSize.y);
  vB = vUv - vec2(0.0, u_texelSize.y);
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const CLEAR_FRAG = `
precision highp float;
uniform sampler2D u_tex;
uniform float u_value;
varying vec2 vUv;
void main() { gl_FragColor = u_value * texture2D(u_tex, vUv); }
`;

const SPLAT_FRAG = `
precision highp float;
uniform sampler2D u_target;
uniform vec2 u_point;
uniform vec3 u_color;
uniform float u_radius;
uniform float u_aspect;
varying vec2 vUv;
void main() {
  vec2 p = vUv - u_point;
  p.x *= u_aspect;
  vec3 splat = exp(-dot(p, p) / u_radius) * u_color;
  vec3 base = texture2D(u_target, vUv).xyz;
  gl_FragColor = vec4(base + splat, 1.0);
}
`;

const ADVECTION_FRAG = `
precision highp float;
uniform sampler2D u_velocity;
uniform sampler2D u_source;
uniform vec2 u_texelSize;
uniform float u_dt;
uniform float u_dissipation;
varying vec2 vUv;
void main() {
  vec2 coord = vUv - u_dt * texture2D(u_velocity, vUv).xy * u_texelSize;
  gl_FragColor = u_dissipation * texture2D(u_source, coord);
  gl_FragColor.a = 1.0;
}
`;

const DIVERGENCE_FRAG = `
precision highp float;
uniform sampler2D u_velocity;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
void main() {
  float L = texture2D(u_velocity, vL).x;
  float R = texture2D(u_velocity, vR).x;
  float T = texture2D(u_velocity, vT).y;
  float B = texture2D(u_velocity, vB).y;
  vec2 C = texture2D(u_velocity, vUv).xy;
  if (vL.x < 0.0) { L = -C.x; }
  if (vR.x > 1.0) { R = -C.x; }
  if (vT.y > 1.0) { T = -C.y; }
  if (vB.y < 0.0) { B = -C.y; }
  float div = 0.5 * (R - L + T - B);
  gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
}
`;

const CURL_FRAG = `
precision highp float;
uniform sampler2D u_velocity;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
void main() {
  float L = texture2D(u_velocity, vL).y;
  float R = texture2D(u_velocity, vR).y;
  float T = texture2D(u_velocity, vT).x;
  float B = texture2D(u_velocity, vB).x;
  float c = R - L - T + B;
  gl_FragColor = vec4(0.5 * c, 0.0, 0.0, 1.0);
}
`;

const VORTICITY_FRAG = `
precision highp float;
uniform sampler2D u_velocity;
uniform sampler2D u_curl;
uniform float u_curlStrength;
uniform float u_dt;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
void main() {
  float L = texture2D(u_curl, vL).x;
  float R = texture2D(u_curl, vR).x;
  float T = texture2D(u_curl, vT).x;
  float B = texture2D(u_curl, vB).x;
  float c = texture2D(u_curl, vUv).x;
  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
  force /= length(force) + 0.0001;
  force *= u_curlStrength * c;
  force.y *= -1.0;
  vec2 vel = texture2D(u_velocity, vUv).xy + force * u_dt;
  gl_FragColor = vec4(vel, 0.0, 1.0);
}
`;

const PRESSURE_FRAG = `
precision highp float;
uniform sampler2D u_pressure;
uniform sampler2D u_divergence;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
void main() {
  float L = texture2D(u_pressure, vL).x;
  float R = texture2D(u_pressure, vR).x;
  float T = texture2D(u_pressure, vT).x;
  float B = texture2D(u_pressure, vB).x;
  float div = texture2D(u_divergence, vUv).x;
  float p = (L + R + T + B - div) * 0.25;
  gl_FragColor = vec4(p, 0.0, 0.0, 1.0);
}
`;

const GRADIENT_SUBTRACT_FRAG = `
precision highp float;
uniform sampler2D u_pressure;
uniform sampler2D u_velocity;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
void main() {
  float L = texture2D(u_pressure, vL).x;
  float R = texture2D(u_pressure, vR).x;
  float T = texture2D(u_pressure, vT).x;
  float B = texture2D(u_pressure, vB).x;
  vec2 vel = texture2D(u_velocity, vUv).xy;
  vel -= vec2(R - L, T - B);
  gl_FragColor = vec4(vel, 0.0, 1.0);
}
`;

// Display: render dye through an iridescent oil-slick palette on black base.
const DISPLAY_FRAG = `
precision highp float;
uniform sampler2D u_dye;
varying vec2 vUv;

vec3 oilPalette(float t) {
  vec3 a = vec3(0.02);
  vec3 b = vec3(0.5);
  vec3 c = vec3(1.6);
  vec3 d = vec3(0.0, 0.33, 0.67);
  return a + b * cos(6.28318 * (c * t + d));
}

void main() {
  vec3 d = texture2D(u_dye, vUv).rgb;
  // Use both magnitude and a channel mix as the palette parameter so the
  // hue shifts as the dye flows — different streaks read as different
  // colours and the sheen feels alive.
  float m = length(d);
  float palT = m * 0.9 + d.x * 0.4 - d.z * 0.2;
  vec3 col = oilPalette(palT);
  // Crush dark areas to true black so the canvas reads as inky.
  col *= smoothstep(0.05, 0.55, m);
  col = pow(col, vec3(1.5));
  gl_FragColor = vec4(col, 1.0);
}
`;

// ---- WebGL helpers -----------------------------------------------------------

interface FBO {
  texture: WebGLTexture;
  fbo: WebGLFramebuffer;
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  attach: (id: number) => number;
}

interface DoubleFBO {
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  read: FBO;
  write: FBO;
  swap: () => void;
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(sh), src);
  }
  return sh;
}

function createProgram(gl: WebGLRenderingContext, vsSrc: string, fsSrc: string) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = gl.createProgram()!;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program));
  }
  // Cache uniform locations.
  const uniforms: Record<string, WebGLUniformLocation | null> = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(program, i)!;
    uniforms[info.name] = gl.getUniformLocation(program, info.name);
  }
  return { program, uniforms };
}

function createFBO(
  gl: WebGLRenderingContext,
  w: number,
  h: number,
  internalFormat: number,
  format: number,
  type: number,
  param: number,
): FBO {
  gl.activeTexture(gl.TEXTURE0);
  const texture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);

  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  gl.viewport(0, 0, w, h);
  gl.clear(gl.COLOR_BUFFER_BIT);

  return {
    texture,
    fbo,
    width: w,
    height: h,
    texelSizeX: 1 / w,
    texelSizeY: 1 / h,
    attach(id: number) {
      gl.activeTexture(gl.TEXTURE0 + id);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      return id;
    },
  };
}

function createDoubleFBO(
  gl: WebGLRenderingContext,
  w: number,
  h: number,
  internalFormat: number,
  format: number,
  type: number,
  param: number,
): DoubleFBO {
  let read = createFBO(gl, w, h, internalFormat, format, type, param);
  let write = createFBO(gl, w, h, internalFormat, format, type, param);
  return {
    width: w,
    height: h,
    texelSizeX: 1 / w,
    texelSizeY: 1 / h,
    get read() { return read; },
    get write() { return write; },
    swap() { const t = read; read = write; write = t; },
  };
}

// ---- Component --------------------------------------------------------------

export default function OilSwab() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      depth: false,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) return;

    // Half-float for HDR-ish precision in the velocity & dye fields. Falls
    // back to byte if unsupported (sim still runs, just less precise).
    const halfFloat = gl.getExtension('OES_texture_half_float');
    gl.getExtension('OES_texture_half_float_linear');
    const hfType = halfFloat ? halfFloat.HALF_FLOAT_OES : gl.UNSIGNED_BYTE;
    const filter = halfFloat ? gl.LINEAR : gl.NEAREST;

    // Fullscreen-quad VBO.
    const quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const elementBuf = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, elementBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 2, 1, 3]), gl.STATIC_DRAW);

    // Build programs.
    const clearProg = createProgram(gl, BASE_VERT, CLEAR_FRAG);
    const splatProg = createProgram(gl, BASE_VERT, SPLAT_FRAG);
    const advectionProg = createProgram(gl, BASE_VERT, ADVECTION_FRAG);
    const divergenceProg = createProgram(gl, BASE_VERT, DIVERGENCE_FRAG);
    const curlProg = createProgram(gl, BASE_VERT, CURL_FRAG);
    const vorticityProg = createProgram(gl, BASE_VERT, VORTICITY_FRAG);
    const pressureProg = createProgram(gl, BASE_VERT, PRESSURE_FRAG);
    const gradSubProg = createProgram(gl, BASE_VERT, GRADIENT_SUBTRACT_FRAG);
    const displayProg = createProgram(gl, BASE_VERT, DISPLAY_FRAG);

    // Simulation resolutions. SIM is the velocity/pressure grid; DYE is the
    // colour field (higher res = sharper streaks).
    const SIM_RES = 128;
    const DYE_RES = 256;

    let velocity = createDoubleFBO(gl, SIM_RES, SIM_RES, gl.RGBA, gl.RGBA, hfType, filter);
    let dye = createDoubleFBO(gl, DYE_RES, DYE_RES, gl.RGBA, gl.RGBA, hfType, filter);
    let divergence = createFBO(gl, SIM_RES, SIM_RES, gl.RGBA, gl.RGBA, hfType, gl.NEAREST);
    let curlFBO = createFBO(gl, SIM_RES, SIM_RES, gl.RGBA, gl.RGBA, hfType, gl.NEAREST);
    let pressure = createDoubleFBO(gl, SIM_RES, SIM_RES, gl.RGBA, gl.RGBA, hfType, gl.NEAREST);

    // Sim tunables — biased for a slow, oily, deep-iridescence look.
    const DT = 0.016;
    const PRESSURE_ITER = 22;
    const VEL_DISSIPATION = 0.99;   // velocity persists longer = slower decay
    const DYE_DISSIPATION = 0.997;  // dye lingers so streaks build up
    const CURL_STRENGTH = 30;
    const SPLAT_RADIUS = 0.015;     // bigger blob per splat
    const SPLAT_FORCE = 7000;

    const blit = (target: FBO | null) => {
      if (target == null) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      } else {
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
        gl.viewport(0, 0, target.width, target.height);
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, elementBuf);
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    };

    // Mouse state — pointer position in canvas-local 0..1, plus the per-event
    // delta used as the splat velocity.
    let mouseDownInside = false;
    let pendingSplats: { x: number; y: number; dx: number; dy: number; color: [number, number, number] }[] = [];
    let lastX = 0.5;
    let lastY = 0.5;

    const queueSplat = (x: number, y: number, dx: number, dy: number) => {
      // Random iridescent colour bias so each streak adds variety to the
      // palette parameter — the display shader maps the dye channels into hue.
      const hue = Math.random();
      const color: [number, number, number] = [
        0.4 + 0.6 * Math.cos(2 * Math.PI * hue),
        0.4 + 0.6 * Math.cos(2 * Math.PI * (hue + 0.33)),
        0.4 + 0.6 * Math.cos(2 * Math.PI * (hue + 0.67)),
      ];
      pendingSplats.push({ x, y, dx, dy, color });
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1 - (e.clientY - rect.top) / rect.height;
      const dx = (x - lastX) * SPLAT_FORCE;
      const dy = (y - lastY) * SPLAT_FORCE;
      lastX = x;
      lastY = y;
      if (mouseDownInside || true) {
        // Always splat on move — feels more responsive than requiring drag.
        queueSplat(x, y, dx, dy);
      }
    };
    const onEnter = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      lastX = (e.clientX - rect.left) / rect.width;
      lastY = 1 - (e.clientY - rect.top) / rect.height;
      mouseDownInside = true;
    };
    const onLeave = () => { mouseDownInside = false; };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseenter', onEnter);
    canvas.addEventListener('mouseleave', onLeave);

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const w = Math.floor(rect.width * dpr);
      const h = Math.floor(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
    };
    resize();

    const applySplat = (s: { x: number; y: number; dx: number; dy: number; color: [number, number, number] }) => {
      const aspect = canvas.width / canvas.height;

      // Splat velocity.
      gl.useProgram(splatProg.program);
      gl.uniform2f(splatProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(splatProg.uniforms.u_target!, velocity.read.attach(0));
      gl.uniform2f(splatProg.uniforms.u_point!, s.x, s.y);
      gl.uniform3f(splatProg.uniforms.u_color!, s.dx, s.dy, 0);
      gl.uniform1f(splatProg.uniforms.u_radius!, SPLAT_RADIUS);
      gl.uniform1f(splatProg.uniforms.u_aspect!, aspect);
      blit(velocity.write);
      velocity.swap();

      // Splat dye.
      gl.uniform2f(splatProg.uniforms.u_texelSize!, dye.texelSizeX, dye.texelSizeY);
      gl.uniform1i(splatProg.uniforms.u_target!, dye.read.attach(0));
      gl.uniform3f(splatProg.uniforms.u_color!, s.color[0], s.color[1], s.color[2]);
      blit(dye.write);
      dye.swap();
    };

    const step = () => {
      gl.disable(gl.BLEND);

      // 1. Curl.
      gl.useProgram(curlProg.program);
      gl.uniform2f(curlProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(curlProg.uniforms.u_velocity!, velocity.read.attach(0));
      blit(curlFBO);

      // 2. Vorticity confinement — feeds curl back into velocity as a force.
      gl.useProgram(vorticityProg.program);
      gl.uniform2f(vorticityProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(vorticityProg.uniforms.u_velocity!, velocity.read.attach(0));
      gl.uniform1i(vorticityProg.uniforms.u_curl!, curlFBO.attach(1));
      gl.uniform1f(vorticityProg.uniforms.u_curlStrength!, CURL_STRENGTH);
      gl.uniform1f(vorticityProg.uniforms.u_dt!, DT);
      blit(velocity.write);
      velocity.swap();

      // 3. Divergence of velocity.
      gl.useProgram(divergenceProg.program);
      gl.uniform2f(divergenceProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(divergenceProg.uniforms.u_velocity!, velocity.read.attach(0));
      blit(divergence);

      // 4. Decay pressure slightly each frame so it doesn't explode, then
      //    Jacobi-iterate.
      gl.useProgram(clearProg.program);
      gl.uniform1i(clearProg.uniforms.u_tex!, pressure.read.attach(0));
      gl.uniform1f(clearProg.uniforms.u_value!, 0.8);
      blit(pressure.write);
      pressure.swap();

      gl.useProgram(pressureProg.program);
      gl.uniform2f(pressureProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(pressureProg.uniforms.u_divergence!, divergence.attach(0));
      for (let i = 0; i < PRESSURE_ITER; i++) {
        gl.uniform1i(pressureProg.uniforms.u_pressure!, pressure.read.attach(1));
        blit(pressure.write);
        pressure.swap();
      }

      // 5. Subtract pressure gradient → divergence-free velocity.
      gl.useProgram(gradSubProg.program);
      gl.uniform2f(gradSubProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(gradSubProg.uniforms.u_pressure!, pressure.read.attach(0));
      gl.uniform1i(gradSubProg.uniforms.u_velocity!, velocity.read.attach(1));
      blit(velocity.write);
      velocity.swap();

      // 6. Advect velocity.
      gl.useProgram(advectionProg.program);
      gl.uniform2f(advectionProg.uniforms.u_texelSize!, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(advectionProg.uniforms.u_velocity!, velocity.read.attach(0));
      gl.uniform1i(advectionProg.uniforms.u_source!, velocity.read.attach(0));
      gl.uniform1f(advectionProg.uniforms.u_dt!, DT);
      gl.uniform1f(advectionProg.uniforms.u_dissipation!, VEL_DISSIPATION);
      blit(velocity.write);
      velocity.swap();

      // 7. Advect dye at the dye resolution.
      gl.uniform2f(advectionProg.uniforms.u_texelSize!, dye.texelSizeX, dye.texelSizeY);
      gl.uniform1i(advectionProg.uniforms.u_velocity!, velocity.read.attach(0));
      gl.uniform1i(advectionProg.uniforms.u_source!, dye.read.attach(1));
      gl.uniform1f(advectionProg.uniforms.u_dissipation!, DYE_DISSIPATION);
      blit(dye.write);
      dye.swap();
    };

    const render = () => {
      gl.useProgram(displayProg.program);
      gl.uniform1i(displayProg.uniforms.u_dye!, dye.read.attach(0));
      blit(null);
    };

    let raf = 0;
    const frame = () => {
      resize();
      for (const s of pendingSplats) applySplat(s);
      pendingSplats = [];
      step();
      render();
      raf = requestAnimationFrame(frame);
    };

    // Seed with a handful of starting splats so the canvas isn't a blank
    // black on first paint.
    for (let i = 0; i < 6; i++) {
      const ang = (i / 6) * Math.PI * 2;
      queueSplat(0.5 + 0.25 * Math.cos(ang), 0.5 + 0.25 * Math.sin(ang),
        -Math.sin(ang) * 1200, Math.cos(ang) * 1200);
    }

    // Idle ambient swirl — without it the canvas would settle into a dead
    // state once the user stops moving. Soft splats every ~700ms keep
    // colour streaks flowing in a slow wandering pattern.
    let nextAmbient = performance.now() + 200;
    const ambientTick = () => {
      const now = performance.now();
      if (now >= nextAmbient) {
        const t = now / 3500;
        const x = 0.5 + 0.35 * Math.cos(t);
        const y = 0.5 + 0.35 * Math.sin(t * 1.3);
        const dx = -Math.sin(t) * 1400;
        const dy = Math.cos(t * 1.3) * 1400;
        queueSplat(x, y, dx, dy);
        nextAmbient = now + 600 + Math.random() * 400;
      }
      ambientRaf = requestAnimationFrame(ambientTick);
    };
    let ambientRaf = requestAnimationFrame(ambientTick);

    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(ambientRaf);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseenter', onEnter);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full aspect-square rounded-xl bg-black" />;
}
